import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SettingsTab = ({ user }) => {
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailEvents: false,
    emailPromotions: false,
    smsReminders: false,
    pushNotifications: false
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState(false);

  // Liability waiver state
  const [waiverData, setWaiverData] = useState({
    hasSigned: true,
    signedDate: '15 Mart 2023',
    expiryDate: '15 Mart 2025'
  });

  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load notification preferences on mount
  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) return;

      const { data, error } = await supabase?.from('delivery_preferences')?.select('notification_email, notification_sms, notification_push')?.eq('user_id', authUser?.id)?.single();

      if (data) {
        setNotificationPrefs({
          emailEvents: data?.notification_email || false,
          emailPromotions: data?.notification_email || false,
          smsReminders: data?.notification_sms || false,
          pushNotifications: data?.notification_push || false
        });
      }
    } catch (err) {
      console.error('Error loading notification preferences:', err);
    }
  };

  // Password change handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors?.[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
    setPasswordSuccess(false);
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    const errors = {};

    if (!passwordData?.currentPassword) {
      errors.currentPassword = 'Mevcut şifre gereklidir';
    }
    if (!passwordData?.newPassword) {
      errors.newPassword = 'Yeni şifre gereklidir';
    } else if (passwordData?.newPassword?.length < 8) {
      errors.newPassword = 'Şifre en az 8 karakter olmalıdır';
    }
    if (!passwordData?.confirmPassword) {
      errors.confirmPassword = 'Şifre tekrarı gereklidir';
    } else if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    if (Object.keys(errors)?.length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordLoading(true);
    setPasswordErrors({});

    try {
      // Update password using Supabase
      const { error } = await supabase?.auth?.updateUser({
        password: passwordData?.newPassword
      });

      if (error) throw error;

      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      console.error('Password update error:', err);
      setPasswordErrors({ submit: err?.message || 'Şifre güncellenirken bir hata oluştu' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Notification preferences handlers
  const handleNotificationChange = (field, checked) => {
    setNotificationPrefs(prev => ({ ...prev, [field]: checked }));
    setNotificationSuccess(false);
  };

  const handleNotificationSubmit = async (e) => {
    e?.preventDefault();
    setNotificationLoading(true);

    try {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) throw new Error('Kullanıcı oturumu bulunamadı');

      // Check if preferences already exist
      const { data: existingPrefs } = await supabase
        ?.from('delivery_preferences')
        ?.select('id')
        ?.eq('user_id', authUser?.id)
        ?.single();

      const prefsData = {
        user_id: authUser?.id,
        notification_email: notificationPrefs?.emailEvents || notificationPrefs?.emailPromotions,
        notification_sms: notificationPrefs?.smsReminders,
        notification_push: notificationPrefs?.pushNotifications,
        updated_at: new Date()?.toISOString()
      };

      let error;
      if (existingPrefs) {
        // Update existing record
        const result = await supabase
          ?.from('delivery_preferences')
          ?.update(prefsData)
          ?.eq('user_id', authUser?.id);
        error = result?.error;
      } else {
        // Insert new record
        const result = await supabase
          ?.from('delivery_preferences')
          ?.insert(prefsData);
        error = result?.error;
      }

      if (error) throw error;

      setNotificationSuccess(true);
      setTimeout(() => setNotificationSuccess(false), 5000);
    } catch (err) {
      console.error('Notification preferences update error:', err);
    } finally {
      setNotificationLoading(false);
    }
  };

  // Account deletion handler
  const handleAccountDelete = async () => {
    if (deleteConfirmText !== 'HESABIMI SIL') {
      return;
    }

    setDeleteLoading(true);

    try {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) throw new Error('Kullanıcı oturumu bulunamadı');

      // Call admin function to delete user account
      const { error } = await supabase?.rpc('admin_delete_user', {
        target_user_id: authUser?.id
      });

      if (error) throw error;

      // Sign out after deletion
      await supabase?.auth?.signOut();
      window.location.href = '/';
    } catch (err) {
      console.error('Account deletion error:', err);
      alert('Hesap silinirken bir hata oluştu: ' + (err?.message || 'Bilinmeyen hata'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Account Settings Section */}
      <div className="bg-card rounded-xl shadow-organic p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Settings" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Hesap Ayarları</h2>
            <p className="text-sm text-muted-foreground">Hesap güvenliğinizi yönetin</p>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Şifre Değiştir</h3>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
            <Input
              type="password"
              label="Mevcut Şifre"
              required
              value={passwordData?.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
              error={passwordErrors?.currentPassword}
              disabled={passwordLoading}
            />

            <Input
              type="password"
              label="Yeni Şifre"
              required
              description="En az 8 karakter"
              value={passwordData?.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
              error={passwordErrors?.newPassword}
              disabled={passwordLoading}
            />

            <Input
              type="password"
              label="Yeni Şifre (Tekrar)"
              required
              value={passwordData?.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
              error={passwordErrors?.confirmPassword}
              disabled={passwordLoading}
            />

            {passwordErrors?.submit && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{passwordErrors?.submit}</p>
              </div>
            )}

            {passwordSuccess && (
              <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-success">Şifreniz başarıyla güncellendi!</p>
              </div>
            )}

            <Button
              type="submit"
              loading={passwordLoading}
              disabled={passwordLoading}
              iconName="Lock"
            >
              Şifreyi Güncelle
            </Button>
          </form>
        </div>
      </div>

      {/* Notification Preferences Section */}
      <div className="bg-card rounded-xl shadow-organic p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Bell" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Bildirim Tercihleri</h2>
            <p className="text-sm text-muted-foreground">Bildirim ayarlarınızı özelleştirin</p>
          </div>
        </div>

        <form onSubmit={handleNotificationSubmit} className="border-t border-border pt-6 space-y-6">
          <div className="space-y-4">
            <Checkbox
              id="emailEvents"
              checked={notificationPrefs?.emailEvents}
              onChange={(e) => handleNotificationChange('emailEvents', e?.target?.checked)}
              label="E-posta ile Etkinlik Bildirimleri"
              description="Yeni etkinlikler ve güncellemeler hakkında bilgi alın"
            />

            <Checkbox
              id="emailPromotions"
              checked={notificationPrefs?.emailPromotions}
              onChange={(e) => handleNotificationChange('emailPromotions', e?.target?.checked)}
              label="E-posta ile Promosyon Bildirimleri"
              description="Özel teklifler ve kampanyalar hakkında bilgi alın"
            />

            <Checkbox
              id="smsReminders"
              checked={notificationPrefs?.smsReminders}
              onChange={(e) => handleNotificationChange('smsReminders', e?.target?.checked)}
              label="SMS Hatırlatmaları"
              description="Etkinlik öncesi SMS hatırlatmaları alın"
            />

            <Checkbox
              id="pushNotifications"
              checked={notificationPrefs?.pushNotifications}
              onChange={(e) => handleNotificationChange('pushNotifications', e?.target?.checked)}
              label="Push Bildirimleri"
              description="Mobil cihazınızda anlık bildirimler alın"
            />
          </div>

          {notificationSuccess && (
            <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
              <p className="text-sm text-success">Bildirim tercihleriniz kaydedildi!</p>
            </div>
          )}

          <Button
            type="submit"
            loading={notificationLoading}
            disabled={notificationLoading}
            iconName="Save"
          >
            Tercihleri Kaydet
          </Button>
        </form>
      </div>

      {/* Liability Waiver Section */}
      <div className="bg-card rounded-xl shadow-organic p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="FileText" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Sorumluluk Muafiyeti</h2>
            <p className="text-sm text-muted-foreground">Muafiyet durumunuzu görüntüleyin</p>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          {waiverData?.hasSigned ? (
            <div className="space-y-4">
              <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-2">Aktif Sorumluluk Muafiyeti</h3>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p><span className="font-medium">İmzalama Tarihi:</span> {waiverData?.signedDate}</p>
                      <p><span className="font-medium">Geçerlilik:</span> {waiverData?.expiryDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  onClick={() => alert('Muafiyet indirme özelliği yakında eklenecek')}
                >
                  Muafiyeti İndir
                </Button>
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  onClick={() => alert('Muafiyet yenileme özelliği yakında eklenecek')}
                >
                  Muafiyeti Yenile
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">Muafiyet Bulunamadı</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Etkinliklere katılabilmek için sorumluluk muafiyeti imzalamanız gerekmektedir.
                  </p>
                  <Button
                    size="sm"
                    iconName="FileSignature"
                    onClick={() => alert('Muafiyet imzalama özelliği yakında eklenecek')}
                  >
                    Muafiyet İmzala
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone Section */}
      <div className="bg-card rounded-xl shadow-organic p-6 border-2 border-destructive/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-destructive/10 rounded-lg">
            <Icon name="AlertTriangle" size={24} className="text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-destructive">Tehlikeli Bölge</h2>
            <p className="text-sm text-muted-foreground">
              Hesabınızı kalıcı olarak silmek istiyorsanız, bu işlem geri alınamaz.
            </p>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          {!showDeleteConfirm ? (
            <Button
              variant="destructive"
              iconName="Trash2"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Hesabı Sil
            </Button>
          ) : (
            <div className="space-y-4 max-w-md">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-foreground mb-2">
                  <strong>Uyarı:</strong> Bu işlem geri alınamaz. Tüm verileriniz kalıcı olarak silinecektir.
                </p>
                <p className="text-sm text-muted-foreground">
                  Devam etmek için <strong>HESABIMI SIL</strong> yazın:
                </p>
              </div>

              <Input
                type="text"
                placeholder="HESABIMI SIL"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e?.target?.value)}
              />

              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  iconName="Trash2"
                  onClick={handleAccountDelete}
                  disabled={deleteConfirmText !== 'HESABIMI SIL' || deleteLoading}
                  loading={deleteLoading}
                >
                  Hesabı Kalıcı Olarak Sil
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={deleteLoading}
                >
                  İptal
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;